import { Solution } from '@energiebespaarders/constants';
import { Calendar, Files, HouseLine, User, UserCircle, UsersThree } from '@phosphor-icons/react';
import Energiepaspoort from '../EnergiepaspoortIcon';
import type { ReactNode } from 'react';

export enum MainNavItem {
  RequestQuotes = 'requestQuotes',
  Financing = 'financing',
  Solutions = 'solutions',
  AboutUs = 'aboutUs',
  Account = 'account',
}

export const mainNavTitles: Record<MainNavItem, ReactNode> = {
  [MainNavItem.RequestQuotes]: 'Offertes aanvragen',
  [MainNavItem.Financing]: 'Financiering',
  [MainNavItem.Solutions]: 'Producten',
  [MainNavItem.AboutUs]: 'Over ons',
  [MainNavItem.Account]: (
    <>
      <User weight="duotone" style={{ verticalAlign: 'text-bottom' }} /> Mijn account
    </>
  ),
};

type NavLink = { label: ReactNode; to: string; isHeading?: boolean };

/* prettier-ignore */
export const requestQuotesLinks: NavLink[][] = [
  [
    { label: 'Opwekken en opslaan', to: '', isHeading: true },
    { label: 'Zonnepanelen offertes', to: '/offertes/zonnepanelen' },
    { label: 'Thuisbatterij offertes', to: '/offertes/thuisbatterij' },
  ],
  [
    { label: 'Verwarmen en koelen', to: '', isHeading: true },
    { label: 'Warmtepomp offertes', to: '/offertes/warmtepomp', },
    { label: 'Airco offertes', to: '/offertes/airco', },
    { label: 'Cv-ketel offertes', to: '/offertes/cv-ketel', },
  ],
  [
    { label: 'Isoleren', to: '', isHeading: true },
    { label: 'Spouwmuurisolatie offertes', to: '/offertes/spouwmuurisolatie', },
    { label: 'Vloer- en bodemisolatie offertes', to: '/offertes/vloerisolatie', },
    { label: 'Dakisolatie offertes', to: '/offertes/dakisolatie', },
    { label: 'Isolatieglas offertes', to: '/offertes/isolatieglas', },
  ],
];

/* prettier-ignore */
export const financingLinks: NavLink[][] = [
  [
    { label: 'Hypotheek', to: '/woning-verduurzamen/financiering/hypotheek', isHeading: true },
    { label: 'Subsidies', to: '/woning-verduurzamen/financiering/subsidies', isHeading: true },
    { label: 'Warmtefonds', to: '/woning-verduurzamen/financiering/energiebespaarlening', isHeading: true },
  ],
];

/* prettier-ignore */
export const solutionsLinks: (NavLink & { solutions?: Solution[] })[][] = [

  [
    { label: 'Isoleren', to: '/woning-verduurzamen/product/isoleren', isHeading: true },
    { label: 'Spouwmuurisolatie', to: '/woning-verduurzamen/product/spouwmuurisolatie', solutions: [Solution.WallInsulation] },
    { label: 'Vloer- en bodemisolatie', to: '/woning-verduurzamen/product/vloerisolatie', solutions: [Solution.FloorInsulation, Solution.CrawlspaceInsulation] },
    { label: 'Dakisolatie', to: '/woning-verduurzamen/product/dakisolatie', solutions: [Solution.InnerRoofInsulation, Solution.AtticFloorInsulation] },
    { label: 'Isolatieglas', to: '/woning-verduurzamen/product/isolatieglas', solutions: [Solution.WindowGlazing, Solution.WindowFrames] },
    // { label: 'Kozijnen', to: '/woning-verduurzamen/product/kozijnen', solutions: [Solution.WindowFrames] }, // TODO:
  ],
  [
    { label: 'Opwekken', to: '/woning-verduurzamen/product/zonnepanelen', isHeading: true }, // TODO: Opwekken en opslaan when we have thuisbatterijen
    { label: 'Zonnepanelen', to: '/woning-verduurzamen/product/zonnepanelen', solutions: [Solution.PvSystem] },
    // { label: 'Thuisbatterijen', to: '/woning-verduurzamen/product/thuisbatterij', solutions: [Solution.HomeBattery] }, // TODO:
  ],
  [
    { label: 'Verwarmen en koelen', to: '/woning-verduurzamen/product/verwarmen-en-koelen', isHeading: true },
    { label: 'Warmtepomp', to: '/woning-verduurzamen/product/warmtepomp', solutions: [Solution.HybridHeatPump, Solution.ElectricHeatPump] },
    { label: 'Airco', to: '/woning-verduurzamen/product/airco', solutions: [Solution.AirToAirHeatPump] },
    { label: 'Cv-ketel', to: '/woning-verduurzamen/product/cv-ketel', solutions: [Solution.CentralHeatingBoiler] },
    // { label: 'Vloerverwarming', to: '/woning-verduurzamen/product/vloerverwarming', solutions: [Solution.UnderfloorHeating] }, // TODO:
  ],

];

export const getSolutionLink = (solution: Solution) =>
  solutionsLinks.flat().find(link => link.solutions?.includes(solution))?.to;

/* prettier-ignore */
export const aboutUsLinks: NavLink[][] = [
  [
    { label: 'Energiebespaarders', to: '', isHeading: true },
    { label: 'Over ons', to: '/over-ons' },
    { label: 'Onze blog', to: '/blog' },
  ],
  [
    { label: 'Samenwerken', to: '', isHeading: true },
    { label: 'Installatiepartner worden', to: '/partner-worden' },
  ],
];

/* prettier-ignore */
export const legacyAccountLinks: NavLink[][] = [
  [
    { label: <><HouseLine /> Mijn verduurzaming</>, to: '/mijn-verduurzaming' },
    { label: <><Files /> Mijn offertes</>, to: '/mijn-offertes' },
    { label: <><Energiepaspoort weight='fill' /> Mijn Energiepaspoort</>, to: '/mijn-energiepaspoort' },
  ],
  [
    { label: <><Calendar /> Mijn afspraken</>, to: '/mijn-afspraken' },
    { label: <><UserCircle /> Mijn gegevens</>, to: '/mijn-gegevens' },
    { label: <><UsersThree /> Bespaarbuddies korting</>, to: '/bespaarbuddies' },
  ],
];

/* prettier-ignore */
export const accountLinks: NavLink[][] = [
  [],
  [
    { label: <><Files /> Mijn mogelijkheden</>, to: '/intake/indicaties' },
    // { label: 'Jouw woning', to: '/intake/indicaties#woning' }, // TODO: Implement this
    { label: <><UserCircle /> Mijn gegevens</>, to: '/mijn-gegevens' },
  ],
];

export const legacyNavMenuLinks: Record<MainNavItem, NavLink[][]> = {
  [MainNavItem.RequestQuotes]: requestQuotesLinks,
  [MainNavItem.Financing]: financingLinks,
  [MainNavItem.Solutions]: solutionsLinks,
  [MainNavItem.AboutUs]: aboutUsLinks,
  [MainNavItem.Account]: legacyAccountLinks,
};

export const navMenuLinks: Record<MainNavItem, NavLink[][]> = {
  [MainNavItem.RequestQuotes]: requestQuotesLinks,
  [MainNavItem.Financing]: financingLinks,
  [MainNavItem.Solutions]: solutionsLinks,
  [MainNavItem.AboutUs]: aboutUsLinks,
  [MainNavItem.Account]: accountLinks,
};
